import axios from 'axios'
import { parseBudget } from '../filmBudget/calculator'
import { getAiToolApiConfig } from './apiConfig'
import { AI_TOOL_TYPES } from './constants'
import { getAiToolFormPath, getAiToolResultsPath } from './resultsPaths'

export { AI_TOOL_TYPES }

const PENDING_PAYMENT_PREFIX = 'aiToolPendingPayment:'
const LEGACY_PENDING_PAYMENT_KEY = 'aiToolPendingPayment'
const COMPLETED_PAYMENT_PREFIX = 'aiToolCompletedPayment:'
const LEGACY_COMPLETED_PAYMENT_KEY = 'aiToolCompletedPayment'
const RESULT_CACHE_PREFIX = 'aiToolResultCache:'
const IN_FLIGHT_PREFIX = 'aiToolResultInFlight:'

const paymentStorage =
  typeof window !== 'undefined' ? window.localStorage : null

function getPendingPaymentKey(paymentId) {
  return `${PENDING_PAYMENT_PREFIX}${paymentId}`
}

function getCompletedPaymentKey(paymentId) {
  return `${COMPLETED_PAYMENT_PREFIX}${paymentId}`
}

function getInFlightKey(paymentId) {
  return `${IN_FLIGHT_PREFIX}${paymentId}`
}

function getResultCacheKey(paymentId) {
  return `${RESULT_CACHE_PREFIX}${paymentId}`
}

function readStorageJson(key) {
  try {
    const raw = paymentStorage?.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    paymentStorage?.removeItem(key)
    return null
  }
}

function readLegacyPendingPayment(expectedPaymentId) {
  const parsed = readStorageJson(LEGACY_PENDING_PAYMENT_KEY)
  if (!parsed?.paymentId) return null
  if (expectedPaymentId && parsed.paymentId !== expectedPaymentId) return null

  paymentStorage?.setItem(
    getPendingPaymentKey(parsed.paymentId),
    JSON.stringify(parsed),
  )
  paymentStorage?.removeItem(LEGACY_PENDING_PAYMENT_KEY)
  return parsed
}

function listStorageKeys(prefix) {
  if (!paymentStorage) return []

  const keys = []
  for (let index = 0; index < paymentStorage.length; index += 1) {
    const key = paymentStorage.key(index)
    if (key?.startsWith(prefix)) {
      keys.push(key)
    }
  }
  return keys
}

export function getCachedAiToolResult(paymentId) {
  if (!paymentId) return null

  try {
    const raw = paymentStorage?.getItem(getResultCacheKey(paymentId))
    return raw ? JSON.parse(raw) : null
  } catch {
    paymentStorage?.removeItem(getResultCacheKey(paymentId))
    return null
  }
}

export function saveCachedAiToolResult(paymentId, resultState) {
  if (!paymentId || !resultState) return
  paymentStorage?.setItem(
    getResultCacheKey(paymentId),
    JSON.stringify(resultState),
  )
}

export function clearCachedAiToolResult(paymentId) {
  if (!paymentId) return
  paymentStorage?.removeItem(getResultCacheKey(paymentId))
}

export function getPaymentCallbackOrigin() {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin
  }

  return process.env.REACT_APP_BASE_URL || ''
}

export function savePendingAiToolPayment(payload) {
  if (!payload?.paymentId) return

  paymentStorage?.setItem(
    getPendingPaymentKey(payload.paymentId),
    JSON.stringify(payload),
  )
}

export function getPendingAiToolPayment(paymentId) {
  if (!paymentId) return null

  const keyed = readStorageJson(getPendingPaymentKey(paymentId))
  if (keyed) return keyed

  return readLegacyPendingPayment(paymentId)
}

export function clearPendingAiToolPayment(paymentId) {
  if (!paymentId) return

  paymentStorage?.removeItem(getPendingPaymentKey(paymentId))

  const legacy = readStorageJson(LEGACY_PENDING_PAYMENT_KEY)
  if (legacy?.paymentId === paymentId) {
    paymentStorage?.removeItem(LEGACY_PENDING_PAYMENT_KEY)
  }
}

export function saveAiToolCompletedPayment(payload) {
  if (!payload?.paymentId) return

  paymentStorage?.setItem(
    getCompletedPaymentKey(payload.paymentId),
    JSON.stringify(payload),
  )

  const legacy = readStorageJson(LEGACY_COMPLETED_PAYMENT_KEY)
  if (legacy?.paymentId === payload.paymentId) {
    paymentStorage?.removeItem(LEGACY_COMPLETED_PAYMENT_KEY)
  }
}

function consumeCompletedPaymentByKey(storageKey, toolType) {
  const parsed = readStorageJson(storageKey)
  if (!parsed) return null
  if (toolType && parsed.toolType !== toolType) return null

  paymentStorage?.removeItem(storageKey)
  return parsed
}

export function consumeAiToolCompletedPayment(toolType, paymentId) {
  if (paymentId) {
    return consumeCompletedPaymentByKey(
      getCompletedPaymentKey(paymentId),
      toolType,
    )
  }

  const keyedMatches = listStorageKeys(COMPLETED_PAYMENT_PREFIX)
    .map((key) => readStorageJson(key))
    .filter((entry) => entry && (!toolType || entry.toolType === toolType))

  if (keyedMatches.length === 1) {
    return consumeCompletedPaymentByKey(
      getCompletedPaymentKey(keyedMatches[0].paymentId),
      toolType,
    )
  }

  return consumeCompletedPaymentByKey(LEGACY_COMPLETED_PAYMENT_KEY, toolType)
}

export function tryMarkAiToolResultInFlight(paymentId) {
  if (!paymentId) return false

  const key = getInFlightKey(paymentId)
  if (paymentStorage?.getItem(key)) return false

  paymentStorage?.setItem(key, Date.now().toString())
  return true
}

export function clearAiToolResultInFlight(paymentId) {
  if (!paymentId) return
  paymentStorage?.removeItem(getInFlightKey(paymentId))
}

export function isAiToolResultInFlight(paymentId) {
  if (!paymentId) return false
  return paymentStorage?.getItem(getInFlightKey(paymentId)) != null
}

export function waitForCachedAiToolResult(
  paymentId,
  { maxWaitMs = 120000, intervalMs = 400 } = {},
) {
  return new Promise((resolve) => {
    if (!paymentId) {
      resolve(null)
      return
    }

    const deadline = Date.now() + maxWaitMs

    const poll = () => {
      const cached = getCachedAiToolResult(paymentId)
      if (cached) {
        resolve(cached)
        return
      }

      const stillWaiting =
        isAiToolResultInFlight(paymentId) ||
        getPendingAiToolPayment(paymentId) != null

      if (Date.now() >= deadline || !stillWaiting) {
        resolve(null)
        return
      }

      setTimeout(poll, intervalMs)
    }

    poll()
  })
}

export function isAiToolPaymentFailureStatus(status) {
  return status === 'failed' || status === 'error'
}

export function getAiToolPaymentErrorMessage(searchParams) {
  if (!searchParams) return 'Payment failed. Please try again.'

  return (
    searchParams.get('paymentError') ||
    searchParams.get('message') ||
    searchParams.get('error_description') ||
    searchParams.get('error') ||
    'Payment failed. Please try again.'
  )
}

export function resolveAiToolFormPath({
  toolType,
  returnPath,
  paymentId,
} = {}) {
  if (toolType) {
    return getAiToolFormPath(toolType)
  }

  const pendingPayment = paymentId ? getPendingAiToolPayment(paymentId) : null
  if (pendingPayment?.toolType) {
    return getAiToolFormPath(pendingPayment.toolType)
  }

  if (returnPath?.endsWith('/results')) {
    return returnPath.replace(/\/results$/, '') || '/'
  }

  return returnPath || '/'
}

export function clearAiToolPaymentFailureState(paymentId) {
  if (!paymentId) return

  clearCachedAiToolResult(paymentId)
  clearAiToolResultInFlight(paymentId)
}

export function getAiToolPaymentCallbackBaseUrl() {
  return `${process.env.REACT_APP_API_URL}/ai-tool/payment/callback`
}

export function buildProductionAnalyzerFormData(form, mode) {
  const isAdv = mode === 'advanced'

  return {
    mode: isAdv ? 'advanced' : 'short',
    uname: form.uname,
    projectTitle: form.projectTitle,
    projectType: form.projectType,
    country: form.country,
    currency: form.currency,
    locations: parseInt(form.locations, 10) || 0,
    shootDays: parseInt(form.shootDays, 10) || 0,
    budget: parseInt(form.budget, 10) || 0,
    actors: parseInt(form.actors, 10) || 0,
    productionMode: form.productionMode,
    crowd: form.crowd,
    night: form.night,
    scenes: parseInt(form.scenes, 10) || 0,
    locationType: form.locationType,
    crew: form.crew,
    post: form.post,
    musicBudget: parseInt(form.musicBudget, 10) || 0,
    postBudget: parseInt(form.postBudget, 10) || 0,
  }
}

export function buildBudgetBreakdownFormData(form, budgetData = null) {
  const formData = {
    uname: form.uname,
    projTitle: form.projTitle,
    format: form.format,
    genre: form.genre,
    market: form.market,
    totalBudget: parseBudget(form.totalBudget),
    shootDays: parseInt(form.shootDays, 10) || 0,
    runtime: form.runtime,
    castCount: parseInt(form.castCount, 10) || 0,
    locations: parseInt(form.locations, 10) || 0,
    nightShoots: parseInt(form.nightShoots, 10) || 0,
    vfxShots: parseInt(form.vfxShots, 10) || 0,
    castLevel: form.castLevel,
    crewLevel: form.crewLevel,
    locationType: form.locationType,
    postLevel: form.postLevel,
    stunts: form.stunts,
    period: form.period,
    music: form.music,
    distrib: form.distrib,
    notes: form.notes,
  }

  if (budgetData) {
    formData.atlPct = budgetData.atlPct
    formData.btlPct = budgetData.btlPct
    formData.postPct = budgetData.postPct
    formData.otherPct = budgetData.otherPct
  }

  return formData
}

export function buildOpportunityEngineFormData({
  uname,
  selCrafts,
  selCT,
  country,
  state,
  city,
  lang,
  recency,
  gender,
  age,
}) {
  return {
    uname,
    crafts: selCrafts,
    contentTypes: selCT,
    country,
    state,
    city,
    language: lang,
    recency,
    gender,
    age,
  }
}

export async function checkOpportunityEngineAvailability({
  email,
  fullName,
  formData,
}) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/ai-tool/opportunity-engine/check-availability`,
    { email, fullName, formData },
    getAiToolApiConfig(),
  )

  return response.data?.data
}

export async function createAiToolOrder({
  toolType,
  email,
  fullName,
  formData,
}) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/ai-tool/payment/create-order`,
    { toolType, email, fullName, formData },
    getAiToolApiConfig(),
  )

  return response.data?.data
}

export const RAZORPAY_EMBEDDED_CHECKOUT_URL =
  'https://api.razorpay.com/v1/checkout/embedded'

/** PitchBlu gold + dark — passed to Razorpay embedded hosted page */
export const RAZORPAY_CHECKOUT_THEME = {
  color: '#FBBD23',
  backdropColor: '#0A0B1E',
}

/** Razorpay embedded checkout loads `image` server-side — must be absolute, public URL. */
function getRazorpayLogoUrl() {
  const origin = getPaymentCallbackOrigin()
  if (!origin) return ''

  return `${origin.replace(/\/$/, '')}/pitchblu-logo.png`
}

function appendRazorpayThemeFields(fields) {
  fields['theme[color]'] = RAZORPAY_CHECKOUT_THEME.color
  fields['theme[backdrop_color]'] = RAZORPAY_CHECKOUT_THEME.backdropColor

  const logoUrl = getRazorpayLogoUrl()
  if (logoUrl) {
    fields.image = logoUrl
  }
}

/** Prefill email/name on embedded checkout; user enters phone on Razorpay if required. */
function appendRazorpayContactFields(fields, { email, fullName }) {
  const trimmedEmail = email?.trim()
  if (!trimmedEmail) {
    throw new Error('Email is required for payment.')
  }

  const trimmedName = fullName?.trim()

  fields['prefill[email]'] = trimmedEmail
  fields['readonly[email]'] = '1'

  if (trimmedName) {
    fields['prefill[name]'] = trimmedName
    fields['readonly[name]'] = '1'
  }
}

/**
 * Redirect flow for WebView / hosted checkout.
 * Razorpay POSTs application/x-www-form-urlencoded to callback_url:
 * - success: razorpay_payment_id, razorpay_order_id, razorpay_signature
 * - failure: error[code], error[description], error[field]
 * Cancel uses GET redirect to cancel_url.
 */
export function submitEmbeddedRazorpayCheckout({
  order,
  email,
  fullName,
  callbackUrl,
  cancelUrl,
  description = 'AI Tool Access',
  notes = {},
}) {
  if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
    throw new Error('Payment gateway is not configured.')
  }

  if (!order?.orderId) {
    throw new Error('Failed to create payment order.')
  }

  if (!callbackUrl) {
    throw new Error('callback_url is required for embedded checkout.')
  }

  const fields = {
    key_id: process.env.REACT_APP_RAZORPAY_KEY_ID,
    amount: Math.round(order.totalAmount * 100),
    currency: order.currency || 'INR',
    order_id: order.orderId,
    name: process.env.REACT_APP_APP_NAME || 'PitchBlu',
    description,
    callback_url: callbackUrl,
    cancel_url: cancelUrl || window.location.href,
  }

  appendRazorpayThemeFields(fields)

  Object.entries(notes).forEach(([key, value]) => {
    fields[`notes[${key}]`] = value
  })

  appendRazorpayContactFields(fields, { email, fullName })

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = RAZORPAY_EMBEDDED_CHECKOUT_URL
  form.style.display = 'none'
  form.target = '_self'

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = String(value)
    form.appendChild(input)
  })

  document.body.appendChild(form)
  form.submit()
  form.remove()
}

export function buildAiToolPaymentCallbackUrl({
  paymentId,
  toolType,
  returnPath,
}) {
  const params = new URLSearchParams()

  if (paymentId) params.set('paymentId', paymentId)
  if (toolType) params.set('toolType', toolType)
  if (returnPath) params.set('returnPath', returnPath)

  const query = params.toString()
  const baseUrl = getAiToolPaymentCallbackBaseUrl()

  return query ? `${baseUrl}?${query}` : baseUrl
}

/** Cancel/back from Razorpay — return to form without a failure message. */
export function buildAiToolPaymentCancelUrl(formPath, paymentId) {
  const origin = getPaymentCallbackOrigin()
  const params = new URLSearchParams({ paymentCancelled: '1' })
  if (paymentId) params.set('paymentId', paymentId)

  return `${origin}${formPath}?${params.toString()}`
}

export function isAiToolPaymentCancelled(searchParams) {
  return searchParams?.get('paymentCancelled') === '1'
}

export async function verifyAiToolPayment({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  paymentId,
}) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/ai-tool/payment/verify`,
    {
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      paymentId,
    },
    getAiToolApiConfig(),
  )

  return response.data
}

export async function runAiTool({
  type,
  email,
  fullName,
  paymentId,
  formData,
}) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/ai-tool`,
    { type, email, fullName, paymentId, formData },
    getAiToolApiConfig(),
  )

  return response.data
}

/** After backend callback verifies payment, run AI using data stored in localStorage. */
export async function completeEmbeddedAiToolPayment({
  paymentId,
  toolType,
} = {}) {
  if (!paymentId) {
    throw new Error('Payment session expired. Please try again.')
  }

  const pendingPayment = getPendingAiToolPayment(paymentId)

  if (!pendingPayment) {
    throw new Error('Payment session expired. Please try again.')
  }

  const resolvedToolType = toolType || pendingPayment.toolType

  if (toolType && pendingPayment.toolType !== toolType) {
    throw new Error('Payment session expired. Please try again.')
  }

  const aiResult = await runAiTool({
    type: resolvedToolType,
    email: pendingPayment.email,
    fullName: pendingPayment.fullName,
    paymentId,
    formData: pendingPayment.formData,
  })

  saveAiToolCompletedPayment({
    paymentId,
    toolType: resolvedToolType,
    aiResult,
    email: pendingPayment.email,
    fullName: pendingPayment.fullName,
    formData: pendingPayment.formData,
  })
  clearPendingAiToolPayment(paymentId)

  return {
    aiResult,
    returnPath: pendingPayment.returnPath,
    toolType: resolvedToolType,
    email: pendingPayment.email,
    fullName: pendingPayment.fullName,
    formData: pendingPayment.formData,
  }
}

export async function processAiToolPayment({
  toolType,
  email,
  fullName,
  formData,
  rawFormValues,
  rawMode,
}) {
  const order = await createAiToolOrder({
    toolType,
    email,
    fullName,
    formData,
  })

  if (!order?.orderId || !order?.paymentId) {
    throw new Error('Failed to create payment order.')
  }

  const returnPath = getAiToolResultsPath(toolType)
  const formPath = getAiToolFormPath(toolType)

  savePendingAiToolPayment({
    toolType,
    email,
    fullName,
    formData,
    rawFormValues,
    rawMode,
    paymentId: order.paymentId,
    returnPath,
  })

  submitEmbeddedRazorpayCheckout({
    order,
    email,
    fullName,
    callbackUrl: buildAiToolPaymentCallbackUrl({
      paymentId: order.paymentId,
      toolType,
      returnPath,
    }),
    cancelUrl: buildAiToolPaymentCancelUrl(formPath, order.paymentId),
    notes: { paymentId: order.paymentId, toolType },
  })

  return { order, redirecting: true }
}
